// CRM Home — operator command centre.
// Left: dashboard with the God's-eye ACTIVITY FEED as the primary element
// (every platform event), then signals + clients as secondary cards.
// Right: always-open analyst panel (HubSpot-style), thread history in localStorage.
// Cross-client (staff view). Reuses /dashboard-v2, /platform/activity, intelligence chat.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import API_BASE from '../../config';
import raptorIcon from '../../assets/raptor.svg';
import raptorMark from '../../assets/raptor-white.svg';

const SIGNAL_COLOURS = {
  funding: { c: 'var(--crm-green)', bg: 'var(--crm-green-bg)', l: 'Funding' },
  leadership_change: { c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)', l: 'Leadership' },
  hiring: { c: 'var(--crm-blue)', bg: 'var(--crm-blue-bg)', l: 'Hiring' },
  tech_change: { c: 'var(--crm-cyan)', bg: 'var(--crm-cyan-bg)', l: 'Tech' },
  news: { c: 'var(--crm-slate)', bg: 'var(--crm-slate-bg)', l: 'News' }
};

// Every platform event -> icon, colour, short label. Mirrors the backend emitters.
const ACTIVITY_STYLE = {
  prospect_added:        { ic: '\u2295', c: 'var(--crm-green)',  bg: 'var(--crm-green-bg)',  l: 'Company added' },
  contact_added:        { ic: '\u2295', c: 'var(--crm-blue)',   bg: 'var(--crm-blue-bg)',   l: 'Contact added' },
  prospect_imported:    { ic: '\u2191', c: 'var(--crm-orange)', bg: 'var(--crm-orange-soft)', l: 'Bulk upload' },
  contacts_uploaded:    { ic: '\u2191', c: 'var(--crm-orange)', bg: 'var(--crm-orange-soft)', l: 'Bulk upload' },
  sequence_generated:   { ic: '\u270e', c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)', l: 'Sequence' },
  sequence_approved:    { ic: '\u2713', c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)', l: 'Sequence approved' },
  sequence_edited:      { ic: '\u270e', c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)', l: 'Sequence edited' },
  sequence_pushed:      { ic: '\u2197', c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)', l: 'Pushed to ManyReach' },
  email_batch_created:  { ic: '\u2709', c: 'var(--crm-cyan)',   bg: 'var(--crm-cyan-bg)',   l: 'Email batch' },
  email_batch_add:      { ic: '\u2709', c: 'var(--crm-cyan)',   bg: 'var(--crm-cyan-bg)',   l: 'Email batch' },
  linkedin_batch_created:{ ic: 'in',    c: 'var(--crm-blue)',   bg: 'var(--crm-blue-bg)',   l: 'LinkedIn batch' },
  linkedin_batch_add:   { ic: 'in',    c: 'var(--crm-blue)',   bg: 'var(--crm-blue-bg)',   l: 'LinkedIn batch' },
  campaign_push:        { ic: '\u2197', c: 'var(--crm-orange)', bg: 'var(--crm-orange-soft)', l: 'Campaign' },
  intelligence_sent:    { ic: '\u2709', c: 'var(--crm-green)',  bg: 'var(--crm-green-bg)',  l: 'Digest sent' },
  intelligence_sweep:   { ic: '\u26a1', c: 'var(--crm-slate)',  bg: 'var(--crm-slate-bg)',  l: 'Sweep' },
  icp_generated:        { ic: '\u25ce', c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)', l: 'ICP generated' },
  reply_received:       { ic: '\u21a9', c: 'var(--crm-green)',  bg: 'var(--crm-green-bg)',  l: 'Reply' },
  discovery_saved:      { ic: '\u25ce', c: 'var(--crm-slate)',  bg: 'var(--crm-slate-bg)',  l: 'Discovery' },
  client_created:       { ic: '\u2605', c: 'var(--crm-amber)',  bg: 'var(--crm-amber-bg)',  l: 'New client' }
};
function actStyle(t) { return ACTIVITY_STYLE[t] || { ic: '\u2022', c: 'var(--crm-slate)', bg: 'var(--crm-slate-bg)', l: (t || 'event').replace(/_/g, ' ') }; }

const STARTERS = [
  'What should I prioritise across all clients today?',
  'Which accounts have the hottest fresh signals?',
  'Research the latest funding news for my top account']

const LS_KEY = 'ce_home_threads_v1';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso), mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60); if (h < 24) return h + 'h ago';
  const days = Math.floor(h / 24); if (days < 30) return days + 'd ago';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
function loadThreads() { try { const r = JSON.parse(localStorage.getItem(LS_KEY)); return Array.isArray(r) ? r : []; } catch (_) { return []; } }
function saveThreads(t) { try { localStorage.setItem(LS_KEY, JSON.stringify(t.slice(0, 40))); } catch (_) {} }

// Tidy noisy detail strings (e.g. strip JSON sector arrays from company-added lines).
function cleanDetail(d) {
  if (!d) return '';
  let s = String(d);
  s = s.replace(/\s*\((\[.*?\])\)\s*$/, (m, arr) => {
    try { const a = JSON.parse(arr); return a.length ? ' (' + a.join(', ') + ')' : ''; } catch (_) { return ''; }
  });
  return s;
}

function Favicon({ website, name, size = 28 }) {
  const [failed, setFailed] = useState(false);
  let domain = '';
  try { domain = website ? new URL(website.startsWith('http') ? website : 'https://' + website).hostname : ''; } catch (_) {}
  const initials = (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  if (!domain || failed) return <div className="crmh-fav crmh-fav--fb" style={{ width: size, height: size }}>{initials}</div>;
  return <img className="crmh-fav" style={{ width: size, height: size }} alt="" src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} onError={() => setFailed(true)} />;
}

function renderInline(line) {
  const tokens = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return tokens.map((t, i) => {
    const b = t.match(/^\*\*([^*]+)\*\*$/);
    return b ? <strong key={i}>{b[1]}</strong> : <span key={i}>{t.replace(/\[#\d+\]/g, '')}</span>;
  });
}
function Answer({ text }) {
  const lines = String(text).replace(/\n{3,}/g, '\n\n').split('\n');
  const out = []; let list = null;
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*]\s+(.*)/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)/);
    const heading = line.match(/^#{1,4}\s+(.*)/);
    if (bullet || numbered) { if (!list) list = []; list.push(<li key={'li' + i} className="crmh-ans-li">{renderInline(bullet ? bullet[1] : numbered[1])}</li>); return; }
    if (list) { out.push(<ul key={'ul' + i} className="crmh-ans-ul">{list}</ul>); list = null; }
    if (heading) { out.push(<div key={'h' + i} className="crmh-ans-h">{renderInline(heading[1])}</div>); return; }
    if (line === '') { out.push(<div key={'sp' + i} className="crmh-ans-sp" />); return; }
    out.push(<p key={'p' + i} className="crmh-ans-p">{renderInline(line)}</p>);
  });
  if (list) out.push(<ul key="ul-end" className="crmh-ans-ul">{list}</ul>);
  return <div className="crmh-ans">{out}</div>;
}

export default function CrmHome() {
  const { clientId } = useOutletContext();
  const navigate = useNavigate();
  const { user } = useUser();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [actLoading, setActLoading] = useState(true);
  const [actHasMore, setActHasMore] = useState(false);

  const [threads, setThreads] = useState(() => loadThreads());
  const [activeId, setActiveId] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const endRef = useRef(null);

  const active = threads.find(t => t.id === activeId) || null;
  const chat = active?.messages || [];

  const load = useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/dashboard-v2`); if (r.ok) setData(await r.json()); }
    catch (_) {} finally { setLoading(false); }
  }, []);
  const loadActivity = useCallback(async (before) => {
    try {
      const u = new URL(`${API_BASE}/platform/activity`);
      u.searchParams.set('limit', '40');
      if (before) u.searchParams.set('before', before);
      const r = await fetch(u);
      if (r.ok) {
        const d = await r.json();
        setActivity(prev => before ? [...prev, ...(d.events || [])] : (d.events || []));
        setActHasMore(!!d.has_more);
      }
    } catch (_) {} finally { setActLoading(false); }
  }, []);

  useEffect(() => { load(); loadActivity(); }, [load, loadActivity]);
  useEffect(() => { saveThreads(threads); }, [threads]);
  useEffect(() => { if (chat.length || busy) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [chat, busy]);
  // light polling so the pulse stays live
  useEffect(() => {
    const id = setInterval(() => { loadActivity(); }, 45000);
    return () => clearInterval(id);
  }, [loadActivity]);

  function newThread() { setActiveId(null); setInput(''); setShowThreads(false); }

  async function send(text) {
    const msg = (text != null ? text : input).trim();
    if (!msg || busy) return;
    let id = activeId;
    if (!id) {
      id = uid();
      const title = msg.length > 42 ? msg.slice(0, 42) + '...' : msg;
      setThreads(t => [{ id, title, messages: [], updated: Date.now() }, ...t]);
      setActiveId(id);
    }
    const history = chat.map(m => ({ role: m.role, content: m.content }));
    setThreads(t => t.map(x => x.id === id ? { ...x, messages: [...x.messages, { role: 'user', content: msg }], updated: Date.now() } : x));
    setInput(''); setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId || (data?.clients?.[0]?.id) || 1}/intelligence/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history })
      });
      const d = await r.json();
      const answer = r.ok ? (d.answer || '') : ('That failed: ' + (d.error || 'unknown'));
      setThreads(t => t.map(x => x.id === id ? { ...x, messages: [...x.messages, { role: 'assistant', content: answer }], updated: Date.now() } : x));
    } catch (_) {
      setThreads(t => t.map(x => x.id === id ? { ...x, messages: [...x.messages, { role: 'assistant', content: 'Network error. Try again.' }], updated: Date.now() } : x));
    } finally { setBusy(false); }
  }
  function deleteThread(e, id) { e.stopPropagation(); setThreads(t => t.filter(x => x.id !== id)); if (activeId === id) setActiveId(null); }

  const signals = data?.today_signals || [];
  const clients = data?.clients || [];
  const pending = data?.pending_sequences || [];
  const priority = data?.priority_contacts || [];
  const name = user?.firstName || 'there';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const tiles = [
    { n: signals.length, l: 'Signals today', c: 'var(--crm-blue)' },
    { n: pending.length, l: 'Approvals waiting', c: 'var(--crm-violet)' },
    { n: priority.length, l: 'Hot prospects', c: 'var(--crm-green)' },
    { n: clients.length, l: 'Clients', c: 'var(--crm-amber)' }
  ];

  return (
    <div className="crmh">
      <div className="crmh-main">
        <div className="crmh-top">
          <div className="crmh-greet">
            <div className="crmh-date">{today}</div>
            <h1 className="crmh-hello">{greeting()}, {name}.</h1>
          </div>
        </div>
        <div className="crmh-tiles">
          {tiles.map((t, i) => (
            <div key={i} className="crmh-tile">
              <div className="crmh-tile-n" style={{ color: t.c }}>{loading ? '–' : t.n}</div>
              <div className="crmh-tile-l">{t.l}</div>
            </div>
          ))}
        </div>

        {/* GOD'S-EYE ACTIVITY FEED — primary element */}
        <div className="crmh-feed">
          <div className="crmh-col-head">
            <span>Platform activity<span className="crmh-feed-live"><span className="crmh-feed-dot" />live</span></span>
          </div>
          <div className="crmh-feed-list">
            {actLoading ? <p className="crmh-empty">Loading the pulse...</p>
              : activity.length === 0 ? <p className="crmh-empty">No activity yet.</p>
              : activity.map(ev => {
                const st = actStyle(ev.action_type);
                return (
                  <div key={ev.id} className="crmh-ev" onClick={() => ev.client_id && navigate(`/clients/${ev.client_id}/crm`)}>
                    <div className="crmh-ev-ic" style={{ color: st.c, background: st.bg }}>{st.ic}</div>
                    <div className="crmh-ev-main">
                      <div className="crmh-ev-top">
                        <span className="crmh-ev-label" style={{ color: st.c }}>{st.l}</span>
                        <span className="crmh-ev-client">{ev.client_name}</span>
                        <span className="crmh-ev-time">{relTime(ev.created_at)}</span>
                      </div>
                      <div className="crmh-ev-detail">{cleanDetail(ev.detail)}</div>
                    </div>
                  </div>
                );
              })}
            {actHasMore && !actLoading && (
              <button className="crmh-feed-more" onClick={() => loadActivity(activity[activity.length - 1]?.created_at)}>Load more</button>
            )}
          </div>
        </div>

        {/* Secondary: signals + clients */}
        <div className="crmh-cols">
          <div className="crmh-col">
            <div className="crmh-col-head"><span>Today's signals</span><button className="crmh-link" onClick={() => navigate(`/clients/${clientId || clients[0]?.id || 1}/crm/intelligence`)}>Open cockpit</button></div>
            <div className="crmh-list">
              {loading ? <p className="crmh-empty">Loading...</p>
                : signals.length === 0 ? <p className="crmh-empty">No new signals in the last 48 hours.</p>
                : signals.slice(0, 5).map(s => {
                  const sc = SIGNAL_COLOURS[s.signal_type] || SIGNAL_COLOURS.news;
                  return (
                    <div key={s.id} className="crmh-sig" onClick={() => navigate(`/clients/${s.client_id}/crm/companies/${s.company_id}`)}>
                      <div className="crmh-sig-top">
                        <span className="crmh-chip" style={{ color: sc.c, background: sc.bg }}>{sc.l}</span>
                        <span className="crmh-sig-score">{s.relevance_score}/10</span>
                        <span className="crmh-sig-meta">{s.client_name} · {relTime(s.created_at)}</span>
                      </div>
                      <div className="crmh-sig-co">{s.company_name}</div>
                      {s.signal_data?.summary && <div className="crmh-sig-sum">{s.signal_data.summary}</div>}
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="crmh-col">
            <div className="crmh-col-head"><span>Clients</span><button className="crmh-link" onClick={() => navigate(`/clients/${clientId || clients[0]?.id || 1}/crm/clients`)}>View all</button></div>
            <div className="crmh-list">
              {loading ? <p className="crmh-empty">Loading...</p>
                : clients.length === 0 ? <p className="crmh-empty">No clients yet.</p>
                : clients.slice(0, 6).map(c => (
                  <div key={c.id} className="crmh-client" onClick={() => navigate(`/clients/${c.id}/crm`)}>
                    <Favicon website={c.website} name={c.name} />
                    <div className="crmh-client-main">
                      <div className="crmh-client-name">{c.name}</div>
                      <div className="crmh-client-meta">{c.company_count || 0} companies · {c.contact_count || 0} contacts</div>
                    </div>
                    <span className={'crmh-status crmh-status--' + (c.status || 'active')}>{c.status || 'active'}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <aside className="crmh-panel">
        <div className="crmh-panel-head">
          <div className="crmh-panel-id">
            <span className="crmh-panel-mark"><img src={raptorMark} alt="Raptor" /></span>
            <span className="crmh-panel-title">Raptor</span>
          </div>
          <div className="crmh-panel-actions">
            <button className="crmh-icon" title="Conversations" onClick={() => setShowThreads(v => !v)}>
              <svg viewBox="0 0 16 16" width="15" height="15"><path d="M2 4h12M2 8h12M2 12h8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
            </button>
            <button className="crmh-icon" title="New chat" onClick={newThread}>
              <svg viewBox="0 0 16 16" width="15" height="15"><path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>
        </div>

        {showThreads && (
          <div className="crmh-threadlist">
            <button className="crmh-thread-new" onClick={newThread}>+ New conversation</button>
            {threads.length === 0 ? <div className="crmh-thread-empty">No past conversations</div>
              : threads.map(t => (
                <div key={t.id} className={'crmh-thread-row' + (t.id === activeId ? ' is-active' : '')} onClick={() => { setActiveId(t.id); setShowThreads(false); }}>
                  <div className="crmh-thread-meta">
                    <div className="crmh-thread-title">{t.title}</div>
                    <div className="crmh-thread-time">{relTime(new Date(t.updated).toISOString())}</div>
                  </div>
                  <button className="crmh-thread-del" title="Delete" onClick={e => deleteThread(e, t.id)}>
                    <svg viewBox="0 0 16 16" width="13" height="13"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  </button>
                </div>
              ))}
          </div>
        )}

        <div className="crmh-panel-body">
          {chat.length === 0 && !busy ? (
            <div className="crmh-panel-intro">
              <div className="crmh-intro-mark"><img src={raptorMark} alt="Raptor" /></div>
              <div className="crmh-intro-title">Raptor</div>
              <div className="crmh-intro-sub">Ask about any client, prospect, or signal across the platform. I can research the web too.</div>
              <div className="crmh-intro-starters">
                {STARTERS.map(s => <button key={s} className="crmh-starter" onClick={() => send(s)}>{s}</button>)}
              </div>
            </div>
          ) : (
            <div className="crmh-thread">
              {chat.map((m, i) => (
                <div key={i} className={'crmh-msg crmh-msg--' + m.role}>
                  {m.role === 'assistant' && <div className="crmh-msg-mark">CE</div>}
                  <div className="crmh-msg-body">{m.role === 'assistant' ? <Answer text={m.content} /> : m.content}</div>
                </div>
              ))}
              {busy && <div className="crmh-msg crmh-msg--assistant"><div className="crmh-msg-mark">CE</div><div className="crmh-msg-body crmh-typing"><span></span><span></span><span></span></div></div>}
              <div ref={endRef} />
            </div>
          )}
        </div>

        <div className="crmh-composer crmh-composer--icon">
          <img className="crm-composer-raptor" src={raptorIcon} alt="Raptor" />
          <textarea
            className="crmh-composer-ta"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask Raptor anything..."
            rows={1}
          />
          <button className="crmh-composer-send" disabled={busy || !input.trim()} onClick={() => send()} title="Send">
            <svg viewBox="0 0 16 16" width="15" height="15"><path d="M8 13V3M8 3L4 7M8 3l4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </aside>
    </div>
  );
}
